import './Board.css'

import React from 'react'
import CommonBoard from '../../common/Board'

const colorByKey = (key, count) => {
  return key === 0 ? 'movable' : key === count - 1 ? 'landing' : 'intermediate'
}

const highlightFields = (props, state) => {
  const fieldColors = {}

  if (state.selectedMove) {
    state.selectedMove.fields().forEach((index, key, all) => {
      if (!props.showAvailableSquares && (key !== 0)) {
        return fieldColors[index]
      }
      fieldColors[index] = colorByKey(key, all.length)
    })
  } else {
    if (state.availableMoves && state.availableMoves.length) {
      state.availableMoves.sort((a, b) => {
        return b.length() - a.length()
      })
      state.availableMoves
        .map((move) => move.fields())
        .forEach((fields) => {
          fields.forEach((f, k, arr) => {
            if (!props.showAvailableSquares && (k !== 0)) {
              return fieldColors[f]
            }
            fieldColors[f] = colorByKey(k, arr.length)
          })
        })
    } else {
      if (props.moves && props.highlightMovable) {
        props.moves.forEach((move) => {
          fieldColors[move.start()] = 'movable'
        })
      }
    }
  }

  return fieldColors
}

export default class Board extends React.Component {
  static getDerivedStateFromProps(props, state) {
    return {
      fields: state.initialized ? state.fields : props.fields,
      rotated: props.rotated,
      playOnWhite: props.playOnWhite,
      firstFieldWhite: props.firstFieldWhite,
      size: props.size,
      disabled: props.disabled,
      moves: props.moves || [],
      fieldColors: highlightFields(props, state),
      dragTargets: props.dragTargets,

      initialized: !!props.fields,
    }
  }

  constructor(props) {
    super(props)

    this.state = {
      fields: this.props.fields,
      highlights: {},
      arrows: [],
      color: null,
      rotated: true,
      playOnWhite: false,
      fieldColors: {},
      disabled: false,
      moves: [],
      selectedPiece: null,
      selectedMove: null,
      animation: null,
    }

    this._lastMove = null
  }

  componentDidMount() {
    this.props.onInterfacePublish({
      animate: (animation) => {
        this.setState({
          animation,
          moves: [],
          selectedPiece: null,
          availableMoves: null,
        })

        return animation.promise
      },
      getActualFields: () => {
        return this.state.fields
      },
    })
  }

  _findMoves(from, to = null) {
    return this.state.moves.filter(
      (move) => from === move.start() && (to === null || to === move.end())
    )
  }

  _onSelectPiece(field) {
    const moves = this._findMoves(field)

    const newState = {
      selectedPiece: field,
      availableMoves: moves,
      lastMovePath: null,
      dragTargets: { [field]: [] },
    }

    moves
      .map((move) => move.fields())
      .forEach((fields) => {
        newState.dragTargets[field].push(fields[fields.length - 1])
      })

    this.setState(newState)
  }

  _onPlayableFieldClicked(field) {
    if (this.state.selectedPiece !== null) {
      this._userMove(this.state.selectedPiece, field)
    }
  }

  _onNonPlayableFieldClicked(field) {
    this.setState({
      selectedPiece: null,
      availableMoves: null,
      arrows: [],
      highlights: {},
      dragTargets: null,
    })
  }

  // MOVES

  _onMovePiece(from, to, piece) {
    if (!this._lastMove) {
      this._lastMove = { from }
    }

    this._lastMove.to = to

    const fields = { ...this.state.fields }
    fields[to] = piece || fields[from]
    delete fields[from]

    this.setState({
      fields,
      highlights: {},
      arrows: [],
      animation: null,
      selectedPiece: null,
      selectedMove: null,
      availableMoves: null,
      dragTargets: null,
    })
  }

  _onRemovePiece(field) {
    const fields = { ...this.state.fields }
    delete fields[field]
    this.setState({ fields })
  }

  _onSetPiece(field, piece) {
    const fields = { ...this.state.fields }
    fields[field] = piece
    this.setState({ fields })
  }

  _onAfterMoveAnimated(fields, lastMovePath) {
    this.setState({
      fields,
      lastMovePath,
      highlights: {},
      arrows: [],
      animation: null,
      selectedPiece: null,
      selectedMove: null,
      availableMoves: null,
      dragTargets: null,
    })
  }

  // ARROWS & HIGHLIGHTS

  _onDrawArrow(arrow) {
    const arrows = [...this.state.arrows]
    arrows.push(arrow)
    this.setState({ arrows })
  }

  _onRemoveArrow(arrow) {
    const arrows = this.state.arrows.filter((v) => v.id !== arrow.id)
    this.setState({ arrows })
  }

  _onHighlightField(field, color) {
    const highlights = { ...this.state.highlights }
    highlights[field] = color
    this.setState({ highlights })
  }

  _onPieceDragEnd(fromIndex, toIndex) {
    this._userMove(fromIndex, toIndex)
  }

  _userMove(fromIndex, toIndex) {
    const moves = this._findMoves(fromIndex, toIndex)

    if (moves.length) {
      this._lastMove = null

      const move = moves[0]

      this.setState({
        selectedMove: move,
        availableMoves: null,
        selectedPiece: null,
      })

      this.props.onUserMove(move)
    }
  }

  render() {
    return (
      <CommonBoard
        playOnWhite={this.state.playOnWhite}
        firstFieldWhite={this.state.firstFieldWhite}
        fieldAlphaNotation={this.props.fieldAlphaNotation}
        size={this.state.size}
        color={this.state.color}
        rotated={this.state.rotated}
        fields={this.state.fields}
        highlights={this.state.highlights}
        arrows={this.state.arrows}
        fieldColors={this.state.fieldColors}
        dragTargets={this.state.dragTargets}
        dragGhost={true}
        animation={this.state.animation}
        lastMovePath={this.props.showLastMove ? this.state.lastMovePath : null}
        features={{
          highlights: true,
          arrows: true,
          edition: false,
          coords: this.props.showCoords,
          availableSquares: this.props.showAvailableSquares
        }}
        onAfterMoveAnimated={this._onAfterMoveAnimated.bind(this)}
        onRemovePiece={this._onRemovePiece.bind(this)}
        onSetPiece={this._onSetPiece.bind(this)}
        onMovePiece={this._onMovePiece.bind(this)}
        onDrawArrow={this._onDrawArrow.bind(this)}
        onRemoveArrow={this._onRemoveArrow.bind(this)}
        onHighlightField={this._onHighlightField.bind(this)}
        onPlayableFieldClicked={this._onPlayableFieldClicked.bind(this)}
        onNonPlayableFieldClicked={this._onNonPlayableFieldClicked.bind(this)}
        onSelectPiece={this._onSelectPiece.bind(this)}
        onPieceDragEnd={this._onPieceDragEnd.bind(this)}
        disabled={this.state.disabled}
      />
    )
  }
}
